import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Spinner, Alert } from "react-bootstrap";
import "./CapTable.scss";

/* ─────────────────────────────────────────────
   Cap Table – React-Bootstrap 1.6.1 + Custom SCSS
   ───────────────────────────────────────────── */

// ── Constants ──
const COLORS = [
  "#F59E0B","#3B82F6","#10B981","#A78BFA","#F472B6","#22D3EE","#FB923C","#A3E635",
  "#EF4444","#6366F1","#14B8A6","#E879F9","#F97316","#84CC16","#EC4899","#06B6D4",
  "#8B5CF6","#D946EF","#0EA5E9","#65A30D"
];
const OTHERS_COLOR = "#C4C7D0";
const SMALL_THRESHOLD = 5;

// ── Helpers ──
function formatDate(ds) {
  if (!ds) return "";
  const d = new Date(ds);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1900) return "";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}
function formatAddr(a) {
  if (!a) return "";
  return [a.Careof, a.Building, a.Street, a.Suburb, a.State, a.Postcode, a.Country]
    .filter(Boolean).join(", ").replace(/,\s*,/g, ",").replace(/^,\s*|,\s*$/g, "");
}
function titleCase(s) {
  if (!s) return "";
  const keep = ["PTY","LTD","ACN","ABN","ATF","AND","T/AS","S/F","P/L"];
  return s.split(" ").map(w => {
    if (keep.includes(w.toUpperCase())) return w.toUpperCase();
    if (w.length <= 2 && w === w.toUpperCase()) return w;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(" ");
}

function parseData(raw) {
  // Handle hierarchy endpoint format: { isSuccess, data: [CompanyData] }
  // The first company in the data array is the focus company
  const d = raw.data?.[0] || raw.Dataobject || raw;

  const company = {
    name: d.CompanyName || d.Companyname,
    acn: d.ACN || d.Acn,
    incorporated: formatDate(d.DateOfIncorporation || d.Dateofincorporation),
    state: d.PlaceOfIncorporation || d.Placeofincorporation || ""
  };

  const classMap = {};
  const shareCapital = d.ShareCapital || d.Sharecapital || [];
  shareCapital.forEach(sc => {
    const classCode = sc.CLASS_CODE || sc.ClassCode;
    const classDesc = sc.CLASS_DESC || sc.ClassDesc || ("Class " + classCode);
    const issued = sc.ISSUED || sc.Issued || 0;
    const paidUp = sc.PAID_UP || sc.PaidUp || 0;
    const nvKey = Object.keys(sc).find(k => k.toLowerCase().replace(/_/g, "") === "nonvotingshares");
    const nonVoting = nvKey ? (sc[nvKey] === true || sc[nvKey] === 1 || sc[nvKey] === "1" || sc[nvKey] === "Y") : false;
    classMap[classCode] = { code: classCode, desc: classDesc.trim(), issued, paidUp, nonVoting, holders: [] };
  });

  const shareholders = d.shareholders || d.Shareholders || [];
  shareholders.forEach(sh => {
    const h = sh.Holdings;
    const cls = h.CLASS_CODE || h.ClassCode;
    if (!classMap[cls]) return;

    const holderNames = sh.Holders.map(hl => {
      // Handle different name formats
      const coName = hl.CO_NAME || hl.CoName;
      const name = hl.Name;
      const givenNames = [hl.GIVEN_NAME1, hl.GIVEN_NAME2, hl.GIVEN_NAME3].filter(Boolean).join(" ");
      const surname = hl.SURNAME;
      const fullName = coName || name || (givenNames && surname ? `${givenNames} ${surname}` : givenNames || surname) || "";
      return fullName.replace(/\s+/g, " ").trim();
    });

    const isJoint = sh.Holders.length > 1;
    const isCorp = sh.Holders.some(hl => (hl.HolderType || hl.Holdertype) === "C");
    let tags = [];
    if (isJoint) tags.push("Joint holding");
    if (isCorp) tags.push("Corporate holder");
    if (h.BEN_OWNER || h.BenOwner) tags.push("Beneficial owner");
    if (h.FULLY_PAID || h.FullyPaid) tags.push("Fully paid");

    const holdersDetail = sh.Holders.map(hl => {
      const coName = hl.CO_NAME || hl.CoName;
      const name = hl.Name;
      const givenNames = [hl.GIVEN_NAME1, hl.GIVEN_NAME2, hl.GIVEN_NAME3].filter(Boolean).join(" ");
      const surname = hl.SURNAME;
      const fullName = coName || name || (givenNames && surname ? `${givenNames} ${surname}` : givenNames || surname) || "";
      const holderType = hl.HolderType || hl.Holdertype;
      const acn = hl.ACN || hl.Acn;
      return {
        name: titleCase(fullName.replace(/\s+/g, " ").trim()),
        type: holderType === "C" ? "Corporate" : "Individual",
        acn: acn && acn.trim() ? acn.trim() : null,
        address: formatAddr(hl.Address),
        dob: formatDate(hl.Dob || hl.DOB),
      };
    });

    // Get beneficial owner name
    const benKey = Object.keys(h).find(k => k.toLowerCase().replace(/_/g, "") === "benname");
    const benName = benKey ? (h[benKey] || "").trim() : "";

    const shares = h.SUM_TOT_SHARES || h.SumTotShares || 0;
    classMap[cls].holders.push({
      name: isJoint ? holderNames.join(" & ") : holderNames[0],
      shares,
      detail: tags.join(" · "),
      benName,
      tt: {
        fullyPaid: h.FULLY_PAID || h.FullyPaid,
        benOwner: h.BEN_OWNER || h.BenOwner,
        benName,
        changeDate: formatDate(h.CHANGE_DATE || h.ChangeDate),
        tranPrice: h.SUM_TRAN_PRICE || h.SumTranPrice || 0,
        totalUnpaid: h.SUM_TOTAL_UNPAID || h.SumTotalUnpaid || 0,
        callOnShares: h.CALL_ON_SHARES || h.CallOnShares,
        isJoint,
        isCorp,
        holders: holdersDetail
      }
    });
  });

  Object.values(classMap).forEach(c => c.holders.sort((a, b) => b.shares - a.shares));
  return { company, classes: Object.values(classMap) };
}

// ── SVG arc helpers ──
function describeArc(cx, cy, r, ir, sa, ea) {
  const sliceAngle = ea - sa;
  const maxGap = 0.018;
  const gap = Math.min(maxGap, sliceAngle * 0.08);
  let s = sa + gap, e = ea - gap;
  if (e <= s) { s = sa + 0.002; e = ea - 0.002; if (e <= s) return ""; }
  const x1o = cx + r * Math.cos(s), y1o = cy + r * Math.sin(s);
  const x2o = cx + r * Math.cos(e), y2o = cy + r * Math.sin(e);
  const x1i = cx + ir * Math.cos(e), y1i = cy + ir * Math.sin(e);
  const x2i = cx + ir * Math.cos(s), y2i = cy + ir * Math.sin(s);
  const large = (e - s) > Math.PI ? 1 : 0;
  return `M ${x1o} ${y1o} A ${r} ${r} 0 ${large} 1 ${x2o} ${y2o} L ${x1i} ${y1i} A ${ir} ${ir} 0 ${large} 0 ${x2i} ${y2i} Z`;
}

function solidArc(cx, cy, r, sa, ea) {
  const sliceAngle = ea - sa;
  const gap = Math.min(0.018, sliceAngle * 0.08);
  const s = sa + gap, e = ea - gap;
  if (e <= s) return "";
  const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
  const large = (e - s) > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

function getFilterOptions(count) {
  const opts = [5, 10, 20, 50].filter(t => t < count);
  opts.push(count);
  return opts;
}

// ── SVG Download ──
function downloadSvg(svgEl, filename) {
  if (!svgEl) return;
  const clone = svgEl.cloneNode(true);
  // Reset any hover states on the clone
  clone.querySelectorAll('.ct-pie-slice--dimmed').forEach(el => el.classList.remove('ct-pie-slice--dimmed'));
  clone.querySelectorAll('.ct-pie-slice--active').forEach(el => el.classList.remove('ct-pie-slice--active'));
  // Add white background rect
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  const vb = clone.getAttribute('viewBox').split(' ');
  bg.setAttribute('width', vb[2]); bg.setAttribute('height', vb[3]);
  bg.setAttribute('fill', '#FFFFFF');
  clone.insertBefore(bg, clone.firstChild);
  // Add xmlns
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/[^a-zA-Z0-9_-]/g, '_') + '.svg';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DownloadSvgButton({ chartRef, filename }) {
  return (
    <button
      className="ct-download-btn"
      onClick={() => {
        const svg = chartRef.current?.querySelector('svg');
        if (svg) downloadSvg(svg, filename);
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 6 }}>
        <path d="M7 1v8m0 0L4 6.5M7 9l3-2.5M2 11.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Download SVG
    </button>
  );
}

// ── Tooltip Component ──
function Tooltip({ data, pos }) {
  if (!data) return null;
  const { holder, total, color, isOthers, othersData } = data;
  if (isOthers) {
    const pct = ((othersData.shares / total) * 100).toFixed(2);
    return (
      <div className="ct-tooltip ct-tooltip--visible" style={{ left: pos.x, top: pos.y }}>
        <div className="ct-tooltip__header"><div className="ct-tooltip__dot" style={{ background: OTHERS_COLOR }} /><div className="ct-tooltip__name">Others ({othersData.count} shareholders)</div><div className="ct-tooltip__pct">{pct}%</div></div>
        <div className="ct-tooltip__body">
          <div className="ct-tooltip__row"><span className="ct-tooltip__label">Combined Shares</span><span className="ct-tooltip__val">{othersData.shares.toLocaleString()}</span></div>
          <div className="ct-tooltip__row"><span className="ct-tooltip__label">Ownership</span><span className="ct-tooltip__val">{pct}%</span></div>
          <div className="ct-tooltip__row"><span className="ct-tooltip__label">Shareholders</span><span className="ct-tooltip__val">{othersData.count}</span></div>
        </div>
      </div>
    );
  }
  const pct = ((holder.shares / total) * 100).toFixed(2);
  const tt = holder.tt;
  return (
    <div className="ct-tooltip ct-tooltip--visible" style={{ left: pos.x, top: pos.y }}>
      <div className="ct-tooltip__header"><div className="ct-tooltip__dot" style={{ background: color }} /><div className="ct-tooltip__name">{titleCase(holder.name)}</div><div className="ct-tooltip__pct">{pct}%</div></div>
      <div className="ct-tooltip__body">
        <div className="ct-tooltip__row"><span className="ct-tooltip__label">Shares Held</span><span className="ct-tooltip__val">{holder.shares.toLocaleString()}</span></div>
        <div className="ct-tooltip__row"><span className="ct-tooltip__label">Ownership</span><span className="ct-tooltip__val">{pct}%</span></div>
        {tt.tranPrice > 0 && <div className="ct-tooltip__row"><span className="ct-tooltip__label">Paid Up Value</span><span className="ct-tooltip__val">${tt.tranPrice.toLocaleString()}</span></div>}
        {tt.totalUnpaid > 0 && <div className="ct-tooltip__row"><span className="ct-tooltip__label">Amount Unpaid</span><span className="ct-tooltip__val">${tt.totalUnpaid.toLocaleString()}</span></div>}
        {tt.changeDate && <div className="ct-tooltip__row"><span className="ct-tooltip__label">Last Change</span><span className="ct-tooltip__val">{tt.changeDate}</span></div>}
        {tt.benOwner && !tt.benName && (
          <div className="ct-tooltip__row ct-tooltip__row--ben"><span className="ct-tooltip__label">Beneficial Status</span><span className="ct-tooltip__val ct-tooltip__val--ben-owned">Ben Owned</span></div>
        )}
        {!tt.benOwner && tt.benName && (<>
          <div className="ct-tooltip__row ct-tooltip__row--ben"><span className="ct-tooltip__label">Beneficial Status</span><span className="ct-tooltip__val ct-tooltip__val--non-ben">Non Ben Owned</span></div>
          <div className="ct-tooltip__row"><span className="ct-tooltip__label">Beneficial Owner</span><span className="ct-tooltip__val ct-tooltip__val--ben-name">{titleCase(tt.benName)}</span></div>
        </>)}
        {!tt.benOwner && !tt.benName && (
          <div className="ct-tooltip__row ct-tooltip__row--ben"><span className="ct-tooltip__label">Beneficial Status</span><span className="ct-tooltip__val ct-tooltip__val--non-ben">Non Ben Owned</span></div>
        )}
        <div className="ct-tooltip__tags">
          {tt.fullyPaid ? <span className="ct-tag ct-tag--green">Fully Paid</span> : <span className="ct-tag ct-tag--amber">Unpaid</span>}
          {tt.benOwner && <span className="ct-tag ct-tag--blue">Beneficial Owner</span>}
          {tt.isJoint && <span className="ct-tag ct-tag--rose">Joint Holding</span>}
          {tt.isCorp && <span className="ct-tag ct-tag--blue">Corporate</span>}
          {tt.callOnShares && <span className="ct-tag ct-tag--amber">Call on Shares</span>}
        </div>
      </div>
      {tt.holders && tt.holders.length > 0 && (
        <div className="ct-tooltip__holders">
          <div className="ct-tooltip__holders-title">{tt.holders.length > 1 ? "Individual Holders" : "Holder Details"}</div>
          {tt.holders.map((h, i) => (
            <div key={i} className="ct-tooltip__holder-entry">
              <div className="ct-tooltip__holder-name">{h.name}</div>
              <div className="ct-tooltip__holder-detail">{[h.type, h.acn && "ACN " + h.acn, h.dob && "DOB: " + h.dob].filter(Boolean).join(" · ")}</div>
              {h.address && <div className="ct-tooltip__holder-detail">{titleCase(h.address)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Donut Pie ──
function DonutChart({ holders, total, pieTotal, highlightIdx, onHover, onLeave, isSingle }) {
  const cx = 130, cy = 130, r = 118, ir = 70;
  if (isSingle) {
    const d1 = `M ${cx+r} ${cy} A ${r} ${r} 0 1 1 ${cx-r} ${cy} L ${cx-ir} ${cy} A ${ir} ${ir} 0 1 0 ${cx+ir} ${cy} Z`;
    const d2 = `M ${cx-r} ${cy} A ${r} ${r} 0 1 1 ${cx+r} ${cy} L ${cx+ir} ${cy} A ${ir} ${ir} 0 1 0 ${cx-ir} ${cy} Z`;
    const color = COLORS[0];
    return (
      <svg viewBox="0 0 260 260" className="ct-pie-svg">
        <g className="ct-single-ring" onMouseEnter={e => onHover(0, e)} onMouseLeave={onLeave}>
          <path d={d1} fill={color} /><path d={d2} fill={color} />
        </g>
      </svg>
    );
  }
  let cum = -Math.PI / 2;
  const sliceData = holders.map((h, i) => {
    const angle = (h.shares / pieTotal) * 2 * Math.PI;
    const midAngle = cum + angle / 2;
    const d = describeArc(cx, cy, r, ir, cum, cum + angle);
    cum += angle;
    return { h, i, d, midAngle };
  });
  const paths = sliceData.map(({ h, i, d, midAngle }) => {
    if (!d) return null;
    const color = h._isOthers ? OTHERS_COLOR : COLORS[i % COLORS.length];
    const isHl = highlightIdx === i;
    const dimmed = highlightIdx !== null && !isHl;
    let style = { transformOrigin: `${cx}px ${cy}px` };
    if (isHl) {
      const dx = Math.cos(midAngle) * 8, dy = Math.sin(midAngle) * 8;
      style = { ...style, transform: `translate(${dx}px, ${dy}px)`, opacity: 1, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' };
    } else if (dimmed) {
      style = { ...style, opacity: 0.25, filter: 'saturate(0.3)' };
    }
    return <path key={i} d={d} fill={color} className="ct-pie-slice" style={style} onMouseEnter={e => onHover(i, e)} onMouseLeave={onLeave} />;
  });
  return <svg viewBox="0 0 260 260" className="ct-pie-svg">{paths}</svg>;
}

// ── Total Securities Solid Pie ──
function TotalSecuritiesPie({ classes, totalShares, highlightIdx, onHover, onLeave }) {
  const cx = 150, cy = 150, r = 118;
  let cum = -Math.PI / 2;
  const sliceMids = [];
  const paths = classes.map((cls, ci) => {
    const angle = (cls.issued / totalShares) * 2 * Math.PI;
    const midAngle = cum + angle / 2;
    const pctVal = (cls.issued / totalShares) * 100;
    sliceMids.push({ ci, midAngle, pctVal, color: COLORS[ci % COLORS.length] });
    const d = solidArc(cx, cy, r, cum, cum + angle);
    cum += angle;
    return d ? (() => {
      const isHl = highlightIdx === ci;
      const dimmed = highlightIdx !== null && !isHl;
      let style = { transformOrigin: `${cx}px ${cy}px` };
      if (isHl) {
        const dx = Math.cos(midAngle) * 8, dy = Math.sin(midAngle) * 8;
        style = { ...style, transform: `translate(${dx}px, ${dy}px)`, opacity: 1, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' };
      } else if (dimmed) {
        style = { ...style, opacity: 0.25, filter: 'saturate(0.3)' };
      }
      return <path key={ci} d={d} fill={COLORS[ci % COLORS.length]} className="ct-pie-slice" style={style} onMouseEnter={() => onHover(ci)} onMouseLeave={onLeave} />;
    })() : null;
  });

  // Callout pegs for small segments
  const smalls = sliceMids.filter(s => s.pctVal < SMALL_THRESHOLD).sort((a, b) => a.midAngle - b.midAngle);
  const baseR = r + 22, step = 22;
  const calloutData = smalls.map((s, idx) => {
    const elbR = baseR + idx * step;
    let elbX = cx + elbR * Math.cos(s.midAngle), elbY = cy + elbR * Math.sin(s.midAngle);
    const dir = Math.cos(s.midAngle) >= 0 ? 1 : -1;
    let labX = elbX + dir * 30, labY = elbY;
    return { ...s, elbX, elbY, dir, labX, labY };
  });
  // Stagger resolve
  for (let pass = 0; pass < 5; pass++) {
    for (let i = 1; i < calloutData.length; i++) {
      const prev = calloutData[i - 1], curr = calloutData[i];
      if (prev.dir === curr.dir) {
        const dy = curr.labY - prev.labY;
        if (Math.abs(dy) < 18) { const sh = (18 - Math.abs(dy)) / 2 + 1; prev.labY -= sh; prev.elbY -= sh * 0.5; curr.labY += sh; curr.elbY += sh * 0.5; }
      }
    }
  }

  const callouts = calloutData.map(({ ci, midAngle, pctVal, color, elbX, elbY, dir, labX, labY }) => {
    const eX = cx + r * Math.cos(midAngle), eY = cy + r * Math.sin(midAngle);
    const pctStr = pctVal.toFixed(pctVal >= 1 ? 1 : 2) + "%";
    const dimmed = highlightIdx !== null && highlightIdx !== ci;
    return (
      <g key={`co-${ci}`} className={dimmed ? "ct-callout--dimmed" : ""} style={{ cursor: "pointer" }} onMouseEnter={() => onHover(ci)} onMouseLeave={onLeave}>
        <polyline points={`${eX},${eY} ${elbX},${elbY} ${labX},${labY}`} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={labX} cy={labY} r="3" fill={color} />
        <text x={labX + dir * 8} y={labY + 1} fill="#6B7084" fontSize="11" fontFamily="DM Sans, sans-serif" fontWeight="600" dominantBaseline="middle" textAnchor={dir >= 0 ? "start" : "end"}>{pctStr}</text>
      </g>
    );
  });

  return <svg viewBox="0 0 340 300" className="ct-ts-pie-svg">{paths}{callouts}</svg>;
}

// ── Legend Row ──
function LegendRow({ holder, total, index, isOthers, highlighted, onHover, onLeave }) {
  const pctVal = (holder.shares / total) * 100;
  const pct = pctVal.toFixed(pctVal >= 10 ? 1 : 2);
  const color = isOthers ? OTHERS_COLOR : COLORS[index % COLORS.length];
  const barW = Math.max(14, Math.round((pctVal / 100) * 44));
  return (
    <div className={`ct-legend-item${highlighted ? " ct-legend-item--hl" : ""}${isOthers ? " ct-legend-item--others" : ""}`} onMouseEnter={e => onHover(index, e)} onMouseLeave={onLeave}>
      <div className="ct-legend-left">
        <div className="ct-legend-pct">{pct} %</div>
        <div className="ct-legend-bar" style={{ background: color, width: barW }} />
      </div>
      <div className="ct-legend-right">
        <div className="ct-legend-name">{titleCase(holder.name)}</div>
        {!isOthers && (holder.tt?.benOwner
          ? <div className="ct-legend-ben ct-legend-ben--owned"><span className="ct-legend-ben__indicator">Ben Owned</span></div>
          : <div className="ct-legend-ben ct-legend-ben--non"><span className="ct-legend-ben__indicator">Non Ben Owned</span>{holder.benName && <>{" · "}<span className="ct-legend-ben__name">{titleCase(holder.benName)}</span></>}</div>
        )}
        <div className="ct-legend-shares">{holder.shares.toLocaleString()} shares</div>
      </div>
    </div>
  );
}

// ── Class Section ──
function ClassSection({ cls, classIndex, onShowTooltip, onHideTooltip }) {
  const total = cls.issued;
  const holderCount = cls.holders.length;
  const needsFilter = holderCount > 5;
  const filterOpts = useMemo(() => getFilterOptions(holderCount), [holderCount]);
  const defaultTop = needsFilter ? (holderCount >= 20 ? 20 : holderCount >= 10 ? 10 : 5) : holderCount;
  const [topN, setTopN] = useState(defaultTop);
  const [hlIdx, setHlIdx] = useState(null);
  const [visible, setVisible] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => setVisible(true), 150 + classIndex * 200); return () => clearTimeout(t); }, [classIndex]);

  const showAll = topN >= holderCount;
  const visibleHolders = showAll ? cls.holders : cls.holders.slice(0, topN);
  const othersHolders = showAll ? [] : cls.holders.slice(topN);
  const othersShares = othersHolders.reduce((s, h) => s + h.shares, 0);
  const displayList = [...visibleHolders];
  let othersData = null;
  if (!showAll && othersHolders.length > 0) {
    othersData = { shares: othersShares, count: othersHolders.length };
    displayList.push({ name: `Others (${othersHolders.length})`, shares: othersShares, detail: `${othersHolders.length} remaining shareholders`, _isOthers: true });
  }
  const pieTotal = displayList.reduce((s, h) => s + h.shares, 0);
  const isSingle = displayList.length === 1 && !displayList[0]._isOthers;
  const visibleShares = visibleHolders.reduce((s, h) => s + h.shares, 0);
  const visiblePct = ((visibleShares / total) * 100).toFixed(1);

  const handlePieHover = useCallback((i, e) => {
    setHlIdx(i);
    const h = displayList[i];
    if (!h) return;
    const color = h._isOthers ? OTHERS_COLOR : COLORS[i % COLORS.length];
    onShowTooltip(h._isOthers ? { isOthers: true, othersData, total, color } : { holder: h, total, color }, e);
  }, [displayList, total, othersData, onShowTooltip]);

  const handleLeave = useCallback(() => { setHlIdx(null); onHideTooltip(); }, [onHideTooltip]);

  return (
    <div className={`ct-section${visible ? " ct-section--visible" : ""}`}>
      <div className="ct-section__header">
        <h2 className="ct-section__title">{cls.desc}</h2>
        <span className="ct-section__meta">
          {total.toLocaleString()} shares issued · {holderCount} shareholders
          {cls.nonVoting && <span className="ct-badge ct-badge--nv">Non Voting</span>}
        </span>
      </div>
      {needsFilter && (
        <div className="ct-filter">
          <label className="ct-filter__label">Show:</label>
          <select className="ct-filter__select" value={topN} onChange={e => setTopN(parseInt(e.target.value))}>
            {filterOpts.map(n => <option key={n} value={n}>{n === holderCount ? `All (${n})` : `Top ${n}`}</option>)}
          </select>
          <span className="ct-filter__info">{showAll ? `Showing all ${holderCount} shareholders` : `Top ${topN} hold ${visiblePct}% of shares`}</span>
        </div>
      )}
      <div className="ct-section__body">
        <div className="ct-chart-wrap" ref={chartRef}>
          <DonutChart holders={displayList} total={total} pieTotal={pieTotal} highlightIdx={hlIdx} onHover={handlePieHover} onLeave={handleLeave} isSingle={isSingle} />
          <div className="ct-chart-center"><div className="ct-chart-center__label">Issued</div><div className="ct-chart-center__value">{total.toLocaleString()}</div></div>
        </div>
        <div className="ct-legend">
          {displayList.map((h, i) => (
            <LegendRow key={i} holder={h} total={total} index={i} isOthers={h._isOthers} highlighted={hlIdx === i} onHover={handlePieHover} onLeave={handleLeave} />
          ))}
        </div>
      </div>
      <div className="ct-section__footer">
        <DownloadSvgButton chartRef={chartRef} filename={cls.desc} />
      </div>
    </div>
  );
}

// ── Total Securities Section ──
function TotalSecurities({ classes, totalShares, onShowTooltip, onHideTooltip }) {
  const [hlIdx, setHlIdx] = useState(null);
  const [visible, setVisible] = useState(false);
  const chartRef = useRef(null);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 100); return () => clearTimeout(t); }, []);

  const handleHover = useCallback((ci) => setHlIdx(ci), []);
  const handleLeave = useCallback(() => setHlIdx(null), []);

  return (
    <div className={`ct-section ct-section--ts${visible ? " ct-section--visible" : ""}`}>
      <div className="ct-section__header">
        <h2 className="ct-section__title">Total Securities</h2>
        <span className="ct-section__meta">{totalShares.toLocaleString()} total shares</span>
      </div>
      <div className="ct-section__body">
        <div className="ct-chart-wrap" ref={chartRef}>
          <TotalSecuritiesPie classes={classes} totalShares={totalShares} highlightIdx={hlIdx} onHover={handleHover} onLeave={handleLeave} />
        </div>
        <div className="ct-ts-legend">
          {classes.map((cls, ci) => {
            const pctVal = (cls.issued / totalShares) * 100;
            const pct = pctVal.toFixed(pctVal >= 10 ? 1 : 2);
            const color = COLORS[ci % COLORS.length];
            const barW = Math.max(3, pctVal);
            return (
              <div key={ci} className={`ct-ts-row${hlIdx === ci ? " ct-ts-row--hl" : ""}`} onMouseEnter={() => handleHover(ci)} onMouseLeave={handleLeave}>
                <div className="ct-ts-row__left">
                  <div className="ct-ts-pct" style={{ color }}>{pct} %</div>
                  <div className="ct-ts-bar-track"><div className="ct-ts-bar-fill" style={{ background: color, width: `${barW}%` }} /></div>
                </div>
                <div className="ct-ts-row__right">
                  <div className="ct-ts-class-name">{cls.desc}{cls.nonVoting && <span className="ct-badge ct-badge--nv ct-badge--sm">Non Voting</span>}</div>
                  <div className="ct-ts-class-shares">{cls.issued.toLocaleString()} shares</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="ct-section__footer">
        <DownloadSvgButton chartRef={chartRef} filename="Total_Securities" />
      </div>
    </div>
  );
}

// ── Main Component ──
export default function CapTable({ data: rawData = null, loading = false, error = null }) {
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const showTooltip = useCallback((data, e) => {
    setTooltipData(data);
    const pad = 16;
    let x = (e?.clientX || 0) + pad, y = (e?.clientY || 0) + pad;
    if (x + 380 > window.innerWidth) x = (e?.clientX || 0) - 380 - pad;
    if (y + 300 > window.innerHeight) y = (e?.clientY || 0) - 300 - pad;
    setTooltipPos({ x: Math.max(pad, x), y: Math.max(pad, y) });
  }, []);
  const hideTooltip = useCallback(() => setTooltipData(null), []);

  useEffect(() => {
    const handler = (e) => { if (tooltipData) { const pad = 16; let x = e.clientX + pad, y = e.clientY + pad; if (x + 380 > window.innerWidth) x = e.clientX - 380 - pad; if (y + 300 > window.innerHeight) y = e.clientY - 300 - pad; setTooltipPos({ x: Math.max(pad, x), y: Math.max(pad, y) }); }};
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [tooltipData]);

  // Loading state
  if (loading) {
    return (
      <div className="ct-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" variant="primary" className="mb-3" />
          <div>Loading cap table...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="ct-page" style={{ padding: '20px' }}>
        <Alert variant="danger">
          <Alert.Heading>Error loading cap table</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  // No data state
  if (!rawData) {
    return (
      <div className="ct-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <div>No cap table data available.</div>
        </div>
      </div>
    );
  }

  const data = parseData(rawData);
  const totalAllShares = data.classes.reduce((s, c) => s + c.issued, 0);
  const numClasses = data.classes.length;

  return (
    <>
      <div className="ct-page">
        <div className="ct-header">
          <h1 className="ct-header__title">{data.company.name}</h1>
          <div className="ct-header__meta">
            <span>ACN {data.company.acn}</span>
            <span>Incorporated {data.company.incorporated}</span>
            <span>{data.company.state}</span>
            {numClasses > 1 && <span className="ct-badge ct-badge--multi">{numClasses} Share Classes</span>}
          </div>
        </div>
        {numClasses > 1 && <TotalSecurities classes={data.classes} totalShares={totalAllShares} onShowTooltip={showTooltip} onHideTooltip={hideTooltip} />}
        {data.classes.map((cls, ci) => (
          <ClassSection key={cls.code} cls={cls} classIndex={ci} onShowTooltip={showTooltip} onHideTooltip={hideTooltip} />
        ))}
        <div className="ct-footer">
          <span>Data source: ASIC company register</span>
          <span>Total shares across all classes: {totalAllShares.toLocaleString()}</span>
        </div>
      </div>
      <Tooltip data={tooltipData} pos={tooltipPos} />
    </>
  );
}
